import { useMemo } from 'react';
import { usePagination, useSortBy, useTable } from 'react-table';

import clsx from 'clsx';

// Hooks
import { useFormatTimeIndicator } from '@/hooks/format/useFormatTimeIndicator';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useFormatNumber, { percentFormat } from '@/utils/hooks/format/useFormatNumber';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
// Components
import Icon from '@/components/base/Icon';
import TablePagination from '@/components/composite/Charts/components/TableChartPagination';

const limitPageSize = 6;

const PaginationTable = ({
    data,
    columnNames,
    userNumbersFormat,
    formatterTranslation = null,
    sortBy,
    timeFormat = () => {
        /** */
    },
    valueFormat = {}
}) => {
    const { t: tAnalytics } = useTranslation('analytics');
    const { t: tCommons } = useTranslation('commons');

    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const { formatDateTime } = useFormatDateTime(userNumbersFormat?.date?.timezone);
    const { formatHour } = useFormatTimeIndicator();

    const columns = useMemo(() => {
        const finalArray = [];
        let originalDataTable = Object.keys(data[0]);

        for (const key of originalDataTable) {
            finalArray.push({
                Header: tAnalytics(columnNames[key]),
                accessor: key,
                Cell: ({ cell, value }) => {
                    switch (cell.column.id) {
                        case 'date':
                            return formatDateTime(value, userNumbersFormat?.date?.format);

                        case 'date_hour':
                            return formatHour(value);

                        case 'time':
                            return timeFormat(value);

                        case 'post_engagements':
                            return tAnalytics(`chart.post_engagements.${value}`);

                        case 'day':
                            return tCommons(`days.${value}`);

                        case 'month':
                            return tAnalytics(`chart.time_unit.${value}`);

                        case 'age':
                        case 'percentage':
                        case 'average_engagement':
                        case 'total':
                        case 'language':
                        case 'hashtag':
                        case 'most_used':
                            return value;
                        case 'average_engagement_rate_per_post':
                            return formatNumber(value, percentFormat);

                        case 'type':
                            if (formatterTranslation) {
                                return t(`${formatterTranslation}.${value}`);
                            }
                            return t(value);

                        case 'reaction':
                            return (
                                <Icon
                                    className="text-base text-icoGray-500 dark:text-icoDarkMode-wolf"
                                    icon={`${value}-reaction`}
                                />
                            );
                        default:
                            if (value || value === 0) {
                                if (valueFormat) {
                                    return formatNumber(value, valueFormat);
                                }
                                return value;
                            }
                            return 'N/A';
                    }
                }
            });
        }
        return finalArray;
    }, []);

    const sortByArray = useMemo(
        () => [
            sortBy || {
                id: columns[1]['accessor'],
                desc: false
            }
        ],

        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        gotoPage,
        pageOptions,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        state: { pageIndex }
    } = useTable(
        {
            columns,
            data: data,
            disableSortRemove: true,
            initialState: { pageSize: limitPageSize, sortBy: sortByArray }
        },
        useSortBy,
        usePagination
    );

    const changePage = (pageNumber) => {
        gotoPage(pageNumber);
    };

    return (
        <div className="flex w-full flex-col p-8">
            <table className="w-full" {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup, index) => (
                        <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column, index) => (
                                <th
                                    className={clsx(
                                        'p-2 text-left text-xs',
                                        column.isSorted
                                            ? 'text-icoBlue dark:text-icoDarkMode-blueStats'
                                            : 'text-icoBlack dark:text-white'
                                    )}
                                    key={index}
                                    {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    <Icon
                                        className="mr-1 align-bottom"
                                        icon={
                                            column.isSorted
                                                ? column.isSortedDesc
                                                    ? 'chevron-bottom'
                                                    : 'chevron-top'
                                                : ''
                                        }
                                    />
                                    {column.render('Header')}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, index) => {
                        prepareRow(row);
                        return (
                            <tr
                                className={clsx(
                                    'border-white bg-icoGray-100 dark:border-icoDarkMode-anthracite dark:bg-icoDarkMode-deepBlack',
                                    index !== 0 ? 'border-t-4' : ''
                                )}
                                key={index}
                                {...row.getRowProps()}>
                                {row.cells.map((cell, index) => {
                                    return (
                                        <td
                                            className="px-3 py-2 font-GMedium text-xs text-icoGray-500 first:font-GSemiBold first:text-icoBlack dark:text-icoDarkMode-wolf"
                                            key={index}
                                            {...cell.getCellProps()}>
                                            {cell?.column?.id === 'user' ? (
                                                <div className="flex items-center">
                                                    {cell.value && cell.value.picture ? (
                                                        <Avatar
                                                            size="xs"
                                                            url={cell.value.picture}
                                                            className="mr-1"
                                                        />
                                                    ) : null}
                                                    {cell?.value?.name}
                                                </div>
                                            ) : (
                                                cell.render('Cell')
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <TablePagination
                pageIndex={pageIndex}
                changePage={changePage}
                pageOptions={pageOptions}
                canNextPage={canNextPage}
                canPreviousPage={canPreviousPage}
                nextPage={nextPage}
                previousPage={previousPage}
            />
        </div>
    );
};

PaginationTable.propTypes = {
    columnNames: PropTypes.object,
    formatterTranslation: PropTypes.string,
    data: PropTypes.array,
    onReset: PropTypes.func,
    timeFormat: PropTypes.func,
    userNumbersFormat: PropTypes.object,
    valueFormat: PropTypes.object,
    network: PropTypes.string,
    config: PropTypes.object,
    isLoading: PropTypes.bool,
    sortBy: PropTypes.shape({
        id: PropTypes.string,
        desc: PropTypes.bool
    })
};

export default PaginationTable;
